/**
 * @format
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  View,
  useWindowDimensions,
  Alert,
} from 'react-native';
import {
  Icon,
  ListItem,
  SearchBar as RNESearchBar,
} from 'react-native-elements';
import { SearchBarBaseProps } from 'react-native-elements/dist/searchbar/SearchBar';
// fix for broken types - https://github.com/react-native-elements/react-native-elements/issues/3089 rne 3.4.2
const SafeSearchBar = RNESearchBar as unknown as React.FC<SearchBarBaseProps>;

import { FlatList } from 'react-native-gesture-handler';
import { search } from 'ss-search';
const debug = require('debug')('se:searchbar');

import {
  SearchBarProps,
  SearchItemType,
  SearchResult,
  SearchResultCollection,
} from './types';
import AppartmentSearchOverlay from './ApartmentSearchOverlay';
import { Advertisement, Provider, getThumbtackPros } from '../../api/adserver';

const placeHolderColor = '#86939e';
const borderColor = '#ffffff';
const lightBackgroundColor = '#ffffff';
const darkBackgroundColor = '#ffffff';

const keyExtractor = (item: SearchResult) =>
  `${item.id.toString()}-${item.itemType.toString()}`;

const windowWidth = 1200;
let leftItemWidth = 50;
let searchInputWidth = windowWidth - leftItemWidth;

const SearchBar: React.FC<SearchBarProps> = (props) => {
  const {
    enableBack,
    initialValue,
    onHasResults,
    onResultPress,
    onBackPressed,
    provider = Provider.Zonetail,
    onThumbtackSearchSubmit,
    setProvider,
    setSearchText,
    onFocus,
    isApartmentSearch,
    setIsApartmentSearch,
  } = props;
  const [query, setQuery] = useState(initialValue || '');
  const [searchResults, setSearchResults] = useState<
    SearchResultCollection | undefined
  >();
  const currWindowWidth = useWindowDimensions().width;
  const [isShowClose, setIsShowClose] = useState<boolean>(true);

  useEffect(() => {
    setQuery(initialValue || '');
  }, [initialValue]);

  if (enableBack) {
    leftItemWidth = 50;
  } else {
    leftItemWidth = 0;
  }

  if (currWindowWidth > windowWidth) {
    searchInputWidth = windowWidth - leftItemWidth;
  } else {
    searchInputWidth = currWindowWidth - leftItemWidth;
  }

  const { sourceCategories, sourceAdvertsList } = props;
  const advertCategories = sourceCategories
    ? sourceCategories.map((category) => {
        return {
          id: category.id,
          title: category.title,
          icon: category.image ? category.image.url : null,
          image: category.image,
        };
      })
    : [];

  const buildSearchResults = (value: string) => {
    // find all categories that "match" string;
    // find all adverts that "match" string;
    const minScore = 0.01;
    // fuseCategories.setCollection(advertCategories);
    // const categoryResultsScored = fuseCategories.search(value);
    // debug(
    //   `search results categories for value: ${value}`,
    //   categoryResultsScored,
    // );

    const categoryResultsScored: any[] = search(
      advertCategories,
      ['title'],
      value,
      {
        withScore: true,
      },
    ) as any[];

    const searchCategories = categoryResultsScored
      .filter((res) => (res.score ? res.score >= minScore : false))
      .map((res) => ({
        title: res.element.title,
        id: res.element.id,
        itemType: SearchItemType.category,
      }));

    // fuseAdverts.setCollection(advertsList);
    //    const advertResultsScored = fuseAdverts.search(value);

    const advertResultsScored: {
      element: Advertisement;
      score: number;
    }[] = search(
      sourceAdvertsList || [],
      ['headline', 'bi_line', 'tags[name]'],
      value,
      {
        withScore: true,
      },
    ) as {
      element: Advertisement;
      score: number;
    }[];

    const searchAdverts = advertResultsScored
      .filter((res) => (res.score ? res.score >= minScore : false))
      .map((res) => ({
        title: res.element.headline,
        subTitle: res.element.bi_line,
        id: res.element.id,
        itemType: SearchItemType.advert,
      }));

    // Sorts search results alphabetacly
    searchAdverts.sort((advertA, advertB) => {
      const uppercaseA = advertA.title.toUpperCase();
      const uppercaseB = advertB.title.toUpperCase();
      return uppercaseA.localeCompare(uppercaseB);
    });
    searchCategories.sort((categoryA, categoryB) => {
      const uppercaseA = categoryA.title.toUpperCase();
      const uppercaseB = categoryB.title.toUpperCase();
      return uppercaseA.localeCompare(uppercaseB);
    });
    return {
      categories: searchCategories,
      adverts: searchAdverts,
      noMatches: searchAdverts.length === 0 && searchCategories.length === 0,
    };
  };

  const onSearchUpdate = (value: string) => {
    debug(`search for: ${value}`);
    if (
      provider === Provider.Thumbtack ||
      provider === Provider.ThumbtackCategory
    ) {
      return;
    }
    if (value && value.length > 2) {
      const results = buildSearchResults(value);
      setSearchResults(results);
      if (onHasResults) {
        onHasResults(
          results.noMatches ||
            results.categories.length > 0 ||
            results.adverts.length > 0,
        );
      }
    } else {
      setSearchResults(undefined);
      if (onHasResults) {
        onHasResults(false);
      }
    }
  };

  //////

  const updateQuery = (input: string) => {
    setQuery(input);
    onSearchUpdate(input);
    setIsShowClose(false);
  };

  let resultData = [];
  const categories = searchResults?.categories || [];
  const adverts = searchResults?.adverts || [];
  const noMatches = searchResults?.noMatches || false;
  if (noMatches) {
    resultData.push({
      id: '1001',
      title: 'Sorry, no results.',
      itemType: SearchItemType.noMatches,
    });
  } else {
    const divider = {
      id: '999',
      title: '',
      itemType: SearchItemType.divider,
    };
    if (adverts.length > 0) {
      resultData.push(...adverts);
    }
    if (categories.length > 0) {
      if (adverts.length > 0) {
        resultData.push(divider);
      }
      resultData.push(...categories);
    }
  }

  const handleOnSubmit = (
    event: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
  ) => {
    if (
      (provider === Provider.Thumbtack ||
        provider === Provider.ThumbtackCategory) &&
      onThumbtackSearchSubmit
    ) {
      //let zipPostal: string;
      if (setSearchText) {
        setSearchText(event.nativeEvent.text);
        setIsShowClose(false);
      }
      setQuery('');
      getThumbtackPros(event.nativeEvent.text)
        .then((res) => {
          if (res) {
            if (provider === Provider.ThumbtackCategory && setProvider) {
              setProvider(Provider.Thumbtack);
            }
            onThumbtackSearchSubmit(res);
          } else {
            onThumbtackSearchSubmit([]);
            setSearchResults(undefined);
          }
        })
        .catch((err) => {
          Alert.alert(
            'Error',
            'Sorry, there was an error fetching the search results.  Please try again shortly.',
          );
          console.log(err);
        });
    }
    console.log(`Searchbar.web - searchSubmit: ${event.nativeEvent.text}`);
    // if (props.onSearchSubmit) {
    //   props.onSearchSubmit(event.nativeEvent.text);
    // }
  };

  const handleOnKeyPress = (
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    console.log(`searchKeyPress: ${event.nativeEvent.key}`);
  };

  const backIcon = (
    <Pressable
      onPress={() => {
        console.log('searchbar on back press');
        if (onBackPressed) {
          onBackPressed();
        }
      }}
    >
      <Icon
        name="arrow-back"
        color={placeHolderColor}
        tvParallaxProperties={null}
      />
    </Pressable>
  );

  const renderItem = useCallback(
    ({ item }: { item: SearchResult }) => {
      let content = null;
      if (item.itemType === SearchItemType.advert) {
        content = (
          <ListItem
            bottomDivider
            containerStyle={styles.listItem}
            tvParallaxProperties={null}
            hasTVPreferredFocus={false}
          >
            <ListItem.Content>
              <ListItem.Title>{item.title.trim()}</ListItem.Title>
              <ListItem.Subtitle>{item.subTitle?.trim()}</ListItem.Subtitle>
            </ListItem.Content>
            <ListItem.Chevron tvParallaxProperties={null} />
          </ListItem>
        );
      } else if (item.itemType === SearchItemType.divider) {
        content = (
          <ListItem
            containerStyle={styles.listItemDivider}
            tvParallaxProperties={null}
            hasTVPreferredFocus={false}
          >
            <ListItem.Content>
              <ListItem.Title>{item.title}</ListItem.Title>
            </ListItem.Content>
          </ListItem>
        );
      } else if (item.itemType === SearchItemType.category) {
        content = (
          <ListItem
            bottomDivider
            tvParallaxProperties={null}
            hasTVPreferredFocus={false}
          >
            <Icon name="search" tvParallaxProperties={null} />
            <ListItem.Content>
              <ListItem.Title>{item.title}</ListItem.Title>
            </ListItem.Content>
          </ListItem>
        );
      } else if (item.itemType === SearchItemType.noMatches) {
        content = (
          <ListItem
            bottomDivider
            tvParallaxProperties={null}
            hasTVPreferredFocus={false}
          >
            <ListItem.Content>
              <ListItem.Title>{item.title}</ListItem.Title>
            </ListItem.Content>
          </ListItem>
        );
      }

      return (
        <Pressable
          onPress={() => {
            Keyboard.dismiss();
            debug('search-bar.web: search bar pressed', item, onResultPress);
            onResultPress && onResultPress(item);
            if (item.itemType === SearchItemType.category) {
              debug('search-bar.web: clearing search results');
              setSearchResults(undefined);
            }
          }}
        >
          {content}
        </Pressable>
      );
    },
    [onResultPress, setSearchResults],
  );

  return (
    <KeyboardAvoidingView
      keyboardVerticalOffset={0}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.wrapper}
    >
      {isApartmentSearch && setIsApartmentSearch ? (
        <AppartmentSearchOverlay
          modalVisible={isApartmentSearch}
          setModalVisible={setIsApartmentSearch}
        />
      ) : (
        <View />
      )}
      <FlatList
        data={resultData}
        keyboardShouldPersistTaps={'handled'}
        keyExtractor={keyExtractor}
        style={styles.resultList}
        renderItem={renderItem}
        contentContainerStyle={{}}
        ListHeaderComponent={
          <View style={styles.searchHeaderContainer}>
            {enableBack && (
              <View style={styles.headerLeftItem}>{backIcon}</View>
            )}
            <SafeSearchBar
              style={[styles.searchBar, props.style]}
              containerStyle={[
                styles.container,
                props.containerStyle,
                { width: searchInputWidth },
              ]}
              inputStyle={[styles.searchBarInput]}
              inputContainerStyle={[
                styles.inputContainer,
                props.inputContainerStyle,
              ]}
              onChangeText={updateQuery}
              value={query}
              placeholder={props.placeholder}
              placeholderTextColor={placeHolderColor}
              platform="default"
              autoCorrect={false}
              autoCapitalize="none"
              autoFocus={false}
              onSubmitEditing={handleOnSubmit}
              onKeyPress={handleOnKeyPress}
              onClear={Keyboard.dismiss}
              onCancel={Keyboard.dismiss}
              onFocus={onFocus}
              clearIcon={isShowClose}
            />
          </View>
        }
        stickyHeaderIndices={[0]}
      />
    </KeyboardAvoidingView>
  );
};

const backgroundColor = '#ffffff';

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: backgroundColor,
    padding: 0,
    margin: 0,
  },
  listContainer: {
    padding: 0,
    margin: 0,
    flex: 1,
  },
  searchBar: {
    fontSize: 14,
    color: placeHolderColor,
    padding: 0,
    margin: 0,
  },
  searchBarInput: {
    borderColor: borderColor,
  },
  resultList: {
    backgroundColor: backgroundColor,
    padding: 0,
    margin: 0,
  },
  inputContainer: {
    backgroundColor: lightBackgroundColor,
    borderWidth: 1,
    borderColor: placeHolderColor,
    borderRadius: 6,
    borderBottomWidth: 1,
    marginTop: 0,
  },
  container: {
    padding: 0,
    paddingRight: 10,
    margin: 0,
    backgroundColor: darkBackgroundColor,
    borderBottomWidth: 0,
    borderTopWidth: 0,
    marginTop: 0,
    width: searchInputWidth,
  },
  headerLeftItem: {
    alignSelf: 'center',
    width: leftItemWidth,
  },
  listItem: {
    marginTop: 0,
  },
  listItemDivider: {
    marginTop: 0,
    backgroundColor: backgroundColor,
  },
  searchHeaderContainer: {
    backgroundColor: darkBackgroundColor,
    flex: 1,
    flexDirection: 'row',
  },
});

export default SearchBar;
